/* Resources: search, blog filters, product slider, article enhancements and calculators. */
(() => {
  if (window.tdResourcesLoaded) return;
  window.tdResourcesLoaded = true;
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

  /* One search box across articles, ingredients and tools. Items come from a JSON script inside the element. */
  class TdResSearch extends HTMLElement {
    connectedCallback() {
      const input = this.querySelector('input');
      const box = this.querySelector('.td-res-search__res');
      const data = this.querySelector('script[type="application/json"]');
      if (!input || !box || !data) return;
      let items = [];
      try { items = JSON.parse(data.textContent); } catch (e) { return; }
      let sel = -1;
      const close = () => { box.hidden = true; input.setAttribute('aria-expanded', 'false'); };
      input.addEventListener('input', () => {
        const q = input.value.trim().toLowerCase();
        sel = -1;
        if (q.length < 2) return close();
        const words = q.split(/\s+/);
        const hits = items
          .map((it) => {
            const hay = (it.t + ' ' + (it.s || '')).toLowerCase();
            if (!words.every((w) => hay.includes(w))) return null;
            return [it, (it.t.toLowerCase().includes(q) ? 3 : 0) + (it.k !== 'Article' ? 1 : 0)];
          })
          .filter(Boolean)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 8)
          .map((x) => x[0]);
        box.innerHTML = hits.length
          ? hits.map((h, i) => `<a href="${esc(h.u)}" role="option" id="${this.id}-o${i}">${h.i ? `<img src="${esc(h.i)}" alt="" loading="lazy">` : ''}<span><small>${esc(h.k)}</small><b>${esc(h.t)}</b></span></a>`).join('')
          : `<div class="td-res-search__empty">No match for "${esc(input.value)}". Try creatine, sleep or ashwagandha.</div>`;
        box.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      });
      input.addEventListener('keydown', (e) => {
        const links = $$('a', box);
        if (e.key === 'Escape') return close();
        if (!links.length || box.hidden) return;
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          sel = (sel + (e.key === 'ArrowDown' ? 1 : -1) + links.length) % links.length;
          links.forEach((l, i) => l.setAttribute('aria-selected', i === sel));
          input.setAttribute('aria-activedescendant', links[sel].id);
        }
        if (e.key === 'Enter') { e.preventDefault(); (links[sel] || links[0]).click(); }
      });
      document.addEventListener('click', (e) => { if (!this.contains(e.target)) close(); });
    }
  }

  /* Blog listing: category pills, sort and load more over server-rendered cards. */
  class TdResBlog extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-grid]');
      if (!this.grid) return;
      this.cards = $$('.td-res-card[data-cat]', this.grid);
      this.promos = $$('[data-promo]', this.grid);
      this.page = parseInt(this.dataset.page || '12', 10);
      this.cat = this.dataset.cat || 'all';
      this.sort = 'new';
      this.shown = this.page;
      this.more = this.querySelector('[data-more]');
      this.count = this.querySelector('[data-count]');
      $$('[data-cat-pill]', this).forEach((p) => p.addEventListener('click', (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
        e.preventDefault();
        this.cat = p.dataset.catPill;
        this.shown = this.page;
        $$('[data-cat-pill]', this).forEach((x) => x.setAttribute('aria-current', x === p));
        history.replaceState(null, '', p.getAttribute('href'));
        this.paint();
      }));
      this.querySelector('[data-sort]')?.addEventListener('change', (e) => { this.sort = e.target.value; this.paint(); });
      this.more?.querySelector('button')?.addEventListener('click', () => { this.shown += this.page; this.paint(); });
      window.addEventListener('resize', () => this.paint());
      this.paint();
    }
    paint() {
      let list = this.cards.filter((c) => this.cat === 'all' || c.dataset.cat === this.cat);
      if (this.sort === 'new') list.sort((a, b) => b.dataset.date - a.dataset.date);
      if (this.sort === 'quick') list.sort((a, b) => a.dataset.mins - b.dataset.mins);
      if (this.sort === 'long') list.sort((a, b) => b.dataset.mins - a.dataset.mins);
      // Fill whole rows: grow the page into the next articles, or drop promo cards on the last page.
      const cols = Math.max(1, getComputedStyle(this.grid).gridTemplateColumns.split(' ').length);
      const fit = (n) => this.promos.filter((p) => n >= parseInt(p.dataset.promo, 10));
      let n = Math.min(this.shown, list.length);
      let promos = fit(n);
      if (n < list.length) n = Math.min(list.length, n + ((cols - ((n + promos.length) % cols)) % cols));
      promos = fit(n);
      while (n === list.length && promos.length && (n + promos.length) % cols) promos = promos.slice(0, -1);
      const visible = list.slice(0, n);
      this.cards.forEach((c) => { c.hidden = !visible.includes(c); });
      const order = [...visible];
      this.promos.forEach((p) => {
        const show = promos.includes(p);
        p.hidden = !show;
        if (show) order.splice(parseInt(p.dataset.promo, 10), 0, p);
      });
      order.forEach((el) => this.grid.appendChild(el));
      if (this.count) this.count.textContent = `Showing ${visible.length} of ${list.length} articles`;
      if (this.more) this.more.querySelector('button').hidden = visible.length >= list.length;
    }
  }

  /* Horizontal slider with arrows that page one card and disable at each end. */
  class TdResSlider extends HTMLElement {
    connectedCallback() {
      const track = this.querySelector('.td-res-slider');
      const nav = this.querySelector('.td-res-snav');
      if (!track || !nav) return;
      const [prev, next] = $$('button', nav);
      const step = () => {
        const c = track.firstElementChild;
        return c ? c.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0) : 300;
      };
      const sync = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        nav.hidden = max <= 0;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max;
      };
      prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    }
  }

  /* Article: table of contents, FAQ accordion with FAQPage schema, references, mid-article product. */
  class TdResArticle extends HTMLElement {
    connectedCallback() {
      const prose = this.querySelector('.td-res-prose');
      if (!prose) return;
      $$('p', prose).forEach((p) => { if (!p.textContent.trim() && !p.querySelector('img, iframe, video')) p.remove(); });
      $$('img', prose).forEach((im) => { if (!im.hasAttribute('alt') || !im.alt.trim()) im.alt = this.dataset.title || ''; im.loading = 'lazy'; });
      const norm = (t) => (t || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
      const firstHead = prose.querySelector('h1, h2');
      if (firstHead && norm(firstHead.textContent) === norm(this.dataset.title)) firstHead.remove();
      $$('h1', prose).forEach((h) => { const h2 = document.createElement('h2'); h2.innerHTML = h.innerHTML; h.replaceWith(h2); });

      const faqs = [];
      const faqH = $$('h2', prose).find((h) => /^(faqs?\b|frequently asked)/i.test(h.textContent.trim()));
      if (faqH && this.querySelector('[data-faq]')) {
        const kill = [faqH];
        let n = faqH.nextElementSibling;
        let cur = null;
        while (n && n.tagName !== 'H2') {
          if (/^H[3-4]$/.test(n.tagName)) { cur = { q: n.textContent.trim(), a: '' }; faqs.push(cur); }
          else if (cur) cur.a += n.outerHTML;
          kill.push(n);
          n = n.nextElementSibling;
        }
        if (faqs.length) {
          kill.forEach((k) => k.remove());
          const wrap = this.querySelector('[data-faq]');
          wrap.querySelector('[data-faq-list]').innerHTML = faqs.map((f, i) => `<details${i === 0 ? ' open' : ''}><summary>${esc(f.q)}</summary><div class="td-res-faq__a">${f.a}</div></details>`).join('');
          wrap.hidden = false;
          const ld = document.createElement('script');
          ld.type = 'application/ld+json';
          ld.textContent = JSON.stringify({ '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() } })) });
          document.head.appendChild(ld);
        }
      }

      const refH = $$('h2, h3', prose).find((h) => /^(references|sources|citations|studies)/i.test(h.textContent.trim()));
      if (refH) {
        const d = document.createElement('details');
        d.className = 'td-res-refs';
        d.innerHTML = '<summary>Scientific references</summary>';
        let n = refH.nextElementSibling;
        while (n && n.tagName !== 'H2') { const nx = n.nextElementSibling; d.appendChild(n); n = nx; }
        refH.replaceWith(d);
      }

      const ownToc = $$('h2', prose).find((h) => /^(table of contents|contents|in this article)$/i.test(h.textContent.trim()));
      if (ownToc && this.querySelector('[data-toc]')) {
        let n = ownToc.nextElementSibling;
        while (n && n.tagName !== 'H2') { const nx = n.nextElementSibling; n.remove(); n = nx; }
        ownToc.remove();
      }
      const heads = $$(':scope > h2', prose);
      heads.forEach((h, i) => { if (!h.id) h.id = 's' + i + '-' + h.textContent.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40); });
      const toc = this.querySelector('[data-toc]');
      if (toc) {
        if (heads.length < 2) toc.remove();
        else {
          toc.querySelector('ol').innerHTML = heads.map((h) => `<li><a href="#${h.id}">${esc(h.textContent.replace(/\s+/g, ' ').trim())}</a></li>`).join('') + (faqs.length ? '<li><a href="#faq">FAQs</a></li>' : '');
          const links = $$('a', toc);
          const io = new IntersectionObserver((es) => es.forEach((e) => {
            if (e.isIntersecting) links.forEach((l) => l.setAttribute('aria-current', l.getAttribute('href') === '#' + e.target.id));
          }), { rootMargin: '-120px 0px -70% 0px' });
          heads.forEach((h) => io.observe(h));
        }
      }

      const inline = this.querySelector('template[data-inline-product]');
      if (inline && heads.length > 2) heads[Math.floor(heads.length / 2)].before(inline.content.cloneNode(true));
    }
  }

  /* Protein and creatine calculators. */
  class TdResCalc extends HTMLElement {
    connectedCallback() {
      const type = this.dataset.calc;
      const w = this.querySelector('[data-weight]');
      const f = this.querySelector('[data-factor]');
      const out = this.querySelector('[data-out]');
      const note = this.querySelector('[data-note]');
      const run = () => {
        const kg = (parseFloat(w.value) || 0) / 2.2046;
        if (type === 'protein') {
          const g = Math.round(kg * parseFloat(f.value));
          out.textContent = g + ' g';
          if (note) note.textContent = `About ${Math.round(g / 4)} g across 4 meals.`;
        } else {
          out.textContent = Math.min(5, Math.max(3, Math.round(kg * 0.04 * 2) / 2)) + ' g';
        }
      };
      [w, f].forEach((el) => el && el.addEventListener('input', run));
      run();
    }
  }

  const define = (n, c) => { if (!customElements.get(n)) customElements.define(n, c); };
  define('td-res-search', TdResSearch);
  define('td-res-blog', TdResBlog);
  define('td-res-slider', TdResSlider);
  define('td-res-article', TdResArticle);
  define('td-res-calc', TdResCalc);
})();
